import { inject, Injectable } from "@angular/core"
import { BehaviorSubject, Subject } from "rxjs"
import { Timestamp } from "firebase/firestore"
import {
  FrontendCard as Card, CardService, Choice,
  DayPlanModule, DeckService, getMer,
  isRegularStudyPhase,
  StudyPhase,
  StudySession,
  StudyService,
  UserService
} from "@nx-superprep/backend/firebase"
import { Timer } from "@nx-superprep/utils"
import { UpdateParams } from "./update-params"

@Injectable({ providedIn: 'root' })
export class StudyStateService {
  private readonly userService = inject(UserService)
  private readonly cardService = inject(CardService)
  private readonly deckService = inject(DeckService)
  private readonly studyService = inject(StudyService)

  private _studyDone = new BehaviorSubject<boolean>(false)
  private _isSaving = false
  public cancelNextCard: Subject<void> = new Subject<void>();

  cancelNextCardOperation(): void {
    this.cancelNextCard.next(); // Emit a value to signal cancellation
    this.cancelNextCard.complete(); // Complete the Subject
  }
  private responseTimer = new Timer()
  private sessionTimer = new Timer()

  // Study state observables
  readonly studyDone$ = this._studyDone.asObservable()

  get isDebugMode() { return this.userService.appSetting.debugMode }

  // Methods for timer management
  startResponseTimer(): void { this.responseTimer.start() }
  stopResponseTimer(): number { this.responseTimer.stop(); return this.responseTimer.value }
  isResponseTimerRunning(): boolean { return this.responseTimer.isRunning() }
  get responseTimerValue(): number { return this.responseTimer.value }
  set responseTimerValue(value: number) { this.responseTimer.value = value }

  startSessionTimer(): void { this.sessionTimer.start() }
  stopSessionTimer(): number { this.sessionTimer.stop(); return this.sessionTimer.value }
  isSessionTimerRunning(): boolean { return this.sessionTimer.isRunning(); }
  get sessionTimerValue(): number { return this.sessionTimer.value }
  set sessionTimerValue(value: number) { this.sessionTimer.value = value }

  markStudyAsDone(): void { this._studyDone.next(true) }

  set isSaving(value: boolean) { this._isSaving = value }
  get isSaving(): boolean { return this._isSaving }

  private _processAnswerResult: { card?: Card, responseTime?: number, index: number, shouldTransition?: boolean, shouldTerminate?: boolean } | null = null;

  get processAnswerResult() {
    return this._processAnswerResult;
  }

  async processCardUpdate(
    card: Card,
    choice: Choice,
    params: UpdateParams
  ): Promise<void> {
    this.isSaving = navigator.onLine // Set saving status

    const {
      responseTime,
      sessionTime,
      phase,
      spacedRep,
      studySetting,
      session,
      module,
      plan,
      studyOrderProgress,
      reposition,
    } = params;

    // Card-specific updates
    if (card && isRegularStudyPhase(phase)) {
      const mer = getMer(
        card.template.sibling,
        studySetting.difficulty,
        spacedRep.cardMER
      );

      card.update({
        phase,
        isCorrect: choice.isAnswer,
        newResponseTime: responseTime,
        speedyFactor: spacedRep.speedyFactor[studySetting.duration],
        nonSpeedyFactor: spacedRep.nonSpeedyFactor[studySetting.duration],
        thirdChanceFactor: spacedRep.thirdChanceFactor[studySetting.duration],
        mer,
      });
    }

    // Update reposition details
    reposition.justAcquiredCardId = card?.justAcquired ? card.id : undefined;
    reposition.lastAnsweredCardId = card?.id;

    // Update session and module stats
    this.updateSessionStats({
      session,
      module,
      choice,
      phase,
      responseTime,
    });

    this.updateModuleStats({
      module,
      session,
      choice,
      phase,
      sessionTime
    });

    // Deck processing
    const rep = this.deckService.processAnswer(choice.isAnswer, responseTime, card);
    this._processAnswerResult = rep;
    reposition.card = rep.card;
    reposition.index = rep.index;
    reposition.responseTime = rep.responseTime;

    // Save user response
    const response = this.cardService.createUserResponse(
      this.userService.activeUser?.uid ?? '',
      card.id,
      responseTime,
      choice.isAnswer,
      phase
    );

    // Persist updates
    return this.studyService.updateUserResponse(
      plan,
      module,
      session,
      response,
      card.stats,
      studyOrderProgress
    ).finally(() => {
      this.isSaving = false;
    });
  }

  private updateSessionStats(params: {
    session: StudySession,
    module: DayPlanModule,
    choice: Choice,
    phase: StudyPhase,
    responseTime: number,
  }): void {
    const { session, module, choice, phase, responseTime } = params;

    // Utility functions for incrementing stats based on phase and answer
    const incCounter = (condition: (phase: StudyPhase, isAnswer: boolean) => boolean, isAnswer: boolean) => condition(phase, isAnswer) ? 1 : 0;

    // Phase-specific increments
    const isAcquisitionPhase = (p: StudyPhase, isAnswer: boolean) => isAnswer && p === StudyPhase.Acquisition
    const isReviewPhase = (p: StudyPhase, isAnswer: boolean) => isAnswer && p === StudyPhase.Review
    const isPracticePhase = (p: StudyPhase, isAnswer: boolean) => isAnswer && !isRegularStudyPhase(p)

    // Update session stats
    session.duration += responseTime
    session.hasEnded = this.checkSessionCompletion(session, module)

    session.acquisitionProgress += incCounter(isAcquisitionPhase, choice.isAnswer)
    session.reviewProgress += incCounter(isReviewPhase, choice.isAnswer)
    session.practiceProgress += incCounter(isPracticePhase, choice.isAnswer)

    session.highestCorrectStreak += choice.isAnswer ? 1 : 0;

    // Detailed progress tracking
    this.updateProgressCounters(session, choice, phase)

    // Remaining targets
    session.remainingAcquisitionTarget = Math.max(0, module.acquisitionTarget - module.acquisitionProgress)
    session.remainingReviewTarget = Math.max(0, module.reviewTarget - module.reviewProgress)
  }

  private updateProgressCounters(
    session: StudySession,
    choice: Choice,
    phase: StudyPhase
  ) {
    const updateCounter = (progressType: 'Acquisition' | 'Review' | 'Practice') => {
      const key = `right${progressType}Count` as keyof StudySession;
      (session as unknown as { [key: string]: number })[key] += choice.isAnswer ? 1 : 0;

      const wrongKey = `wrong${progressType}Count` as keyof StudySession;
      (session as unknown as { [key: string]: number })[wrongKey] += choice.isAnswer ? 0 : 1;
    };

    if (phase === StudyPhase.Acquisition) updateCounter('Acquisition');
    if (phase === StudyPhase.Review) updateCounter('Review');
    if (!isRegularStudyPhase(phase)) updateCounter('Practice');
  }

  private checkSessionCompletion(session: StudySession, module: DayPlanModule): boolean {
    // Implement proper session completion logic
    if (session.hasEnded) return true;

    const acquisitionComplete = module.acquisitionTarget > 0 &&
      module.acquisitionProgress >= module.acquisitionTarget;

    const reviewComplete = module.reviewTarget > 0 &&
      module.reviewProgress >= module.reviewTarget;

    return acquisitionComplete && reviewComplete;
  }

  private updateModuleStats(params: {
    module: DayPlanModule,
    session: StudySession,
    choice: Choice,
    phase: StudyPhase,
    sessionTime: number
  }): void {
    const { module, choice, phase, sessionTime } = params;

    module.actualStudyTime = sessionTime;
    module.highestCorrectStreak += choice.isAnswer ? 1 : 0;

    // Progress tracking
    module.acquisitionProgress += phase === StudyPhase.Acquisition ? 1 : 0;
    module.reviewProgress += phase === StudyPhase.Review ? 1 : 0;
    module.lastStudyDate = Timestamp.now();

    // Completion checks
    module.isReviewFinished = module.reviewProgress >= module.reviewTarget;
    module.isAcquisitionFinished = module.acquisitionProgress >= module.acquisitionTarget;
    module.isFinished = module.isReviewFinished && module.isAcquisitionFinished;

    // Progress calculation
    const total = module.reviewTarget + module.acquisitionTarget;
    module.progress = module.isFinished
      ? 1
      : Math.max(
          module.progress,
          total > 0
            ? Math.min(1, (module.reviewProgress + module.acquisitionProgress) / total)
            : 0
        );
  }
}
